/**
 * OperationalDefaultsStrip — Feb 2026.
 *
 * Read-only strip shown below the TaxCollectorPicker's linked badge.
 * Resolves the (jurisdiction, collector) operational defaults via the
 * backend `/api/tax-collectors/_resolve-defaults` endpoint and renders
 * MLC Fee · ETA · SASE · Instructions with per-field provenance tags.
 *
 * Strict view-only: processors cannot modify these values on the order.
 * To change them, Ops edits Admin → Tax Collectors (collector default)
 * or Admin → Jurisdictions (jurisdiction override). Precedence:
 *   1. jurisdiction.mlc_charges    → "jurisdiction_override" (amber)
 *   2. collector.default_mlc_fee   → "collector_default"     (slate)
 *   3. unset                       → "—"                      (slate, italic)
 *
 * --------------------------------------------------------------------
 * Feb 7, 2026 — Option B fix (Delinquent MLC RCA).
 * --------------------------------------------------------------------
 * The Delinquent Taxes Payable To card used to feed the strip the
 * property's *current-taxes* jurisdiction ID, so its `mlc_charges`
 * override (e.g. $30 on the Township) was incorrectly applied to the
 * delinquent collector — even though the delinquent collector lives
 * in a *different* jurisdiction and the user couldn't see where the
 * $30 was coming from.
 *
 * Fix: when `taxPeriod === "delinquent"` the strip suppresses the
 * `jurisdiction_id` payload to the resolver. The backend then falls
 * back to step 2 (`collector.default_mlc_fee`) or step 3 (unset).
 * Property-jurisdiction overrides intentionally do not flow into the
 * delinquent panel.
 *
 * Until a `delinquent_jurisdiction_id` is tracked on the order
 * (separate Phase C work, not in scope here), this is the correct
 * resolution boundary for the delinquent panel.
 */
import React, { useEffect, useState } from 'react';
import { resolveCollectorDefaults } from '../../utils/taxCollectorsApi';
import { Settings2, ChevronDown, Database, CalendarDays, FileText, ListChecks } from 'lucide-react';

const PROVENANCE_LABEL = {
  jurisdiction_override: 'from jurisdiction override',
  collector_default:     'from collector default',
  unset:                 'unset',
};

// Option B (Feb 7, 2026): the Delinquent panel never resolves a
// `jurisdiction_override` (the property-jurisdiction override is
// intentionally suppressed there — see file-level header), so the
// labels that *can* appear are sharper: they read "from delinquent
// collector default" or "no delinquent fee configured", removing the
// ambiguity the previous shared label had.
const DELINQUENT_PROVENANCE_LABEL = {
  collector_default: 'from delinquent collector default',
  unset:             'no delinquent fee configured',
};

const labelFor = (provenance, taxPeriod) => {
  if (taxPeriod === 'delinquent') {
    return DELINQUENT_PROVENANCE_LABEL[provenance] || PROVENANCE_LABEL[provenance] || '—';
  }
  return PROVENANCE_LABEL[provenance] || '—';
};

const fmtFee = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtEta = (v) => (v == null ? '—' : `${v} day${v === 1 ? '' : 's'}`);
const fmtSase = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

const OperationalDefaultsStrip = ({
  jurisdictionId,
  collectorId,
  taxPeriod = 'current',
  onResolvedChange,
  testIdPrefix = 'operational-defaults-strip',
}) => {
  const [resolved, setResolved] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!collectorId) { setResolved(null); return; }
    // Option B (Feb 7, 2026): the Delinquent panel must not inherit
    // the property's current-taxes jurisdiction override. Drop the
    // jurisdiction_id so the resolver falls back to collector default
    // / unset. See file-level header for rationale.
    const effectiveJurisdictionId =
      taxPeriod === 'delinquent' ? null : jurisdictionId;
    (async () => {
      try {
        const r = await resolveCollectorDefaults({
          jurisdictionId: effectiveJurisdictionId,
          collectorId,
        });
        if (!cancelled) {
          setResolved(r);
          if (onResolvedChange) onResolvedChange(r);
        }
      } catch (_) {
        if (!cancelled) setResolved(null);
      }
    })();
    return () => { cancelled = true; };
  }, [jurisdictionId, collectorId, taxPeriod, onResolvedChange]);

  if (!collectorId || !resolved) return null;

  const feeField  = resolved.mlc_fee || {};
  const etaField  = resolved.eta_days || {};
  const saseField = resolved.sase_required || {};
  const instrField = resolved.request_instructions || {};
  const hasInstr  = !!(instrField.value && String(instrField.value).trim());
  const isOverride = feeField.provenance === 'jurisdiction_override';
  const periodLabel = taxPeriod === 'delinquent' ? 'Delinquent' : 'Current';

  const tiles = [
    { key: 'mlc-fee', label: 'MLC Fee', value: fmtFee(feeField.value), Icon: Database, tone: 'tcp-tile-indigo', bg: 'bg-indigo-50/50 border-indigo-100', prov: feeField.provenance, ov: feeField.provenance === 'jurisdiction_override' },
    { key: 'eta', label: 'ETA', value: fmtEta(etaField.value), Icon: CalendarDays, tone: 'tcp-tile-violet', bg: 'bg-violet-50/50 border-violet-100', prov: etaField.provenance, ov: etaField.provenance === 'jurisdiction_override' },
    { key: 'sase', label: 'SASE', value: fmtSase(saseField.value), Icon: FileText, tone: 'tcp-tile-green', bg: 'bg-emerald-50/50 border-emerald-100', prov: saseField.provenance, ov: saseField.provenance === 'jurisdiction_override' },
  ];

  return (
    <div
      className={`rounded-xl border bg-white ${isOverride ? 'border-amber-200' : 'border-slate-200/80'} shadow-sm`}
      data-testid={`${testIdPrefix}-root`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-toggle`}
      >
        <span className="tcp-tile tcp-tile-blue" style={{ width: 26, height: 26 }}><Settings2 className="w-3.5 h-3.5" /></span>
        <span className="text-[13px] font-semibold text-slate-800">Operational Defaults ({periodLabel})</span>
        {isOverride && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">property override</span>}
        <ChevronDown className={`ml-auto h-4 w-4 text-slate-400 tcp-anim ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`tcp-collapse ${open ? 'tcp-collapse-open' : ''}`}>
        <div className="px-3 pb-3">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {tiles.map((t) => (
              <div key={t.key} className={`tcp-info-tile ${t.bg}`} data-testid={`${testIdPrefix}-${t.key}`} title={labelFor(t.prov, taxPeriod)}>
                <span className={`tcp-tile ${t.tone}`} style={{ width: 30, height: 30 }}><t.Icon className="w-4 h-4" /></span>
                <div className="min-w-0">
                  <div className="text-[11px] font-medium text-slate-500">{t.label}</div>
                  <div className={`text-[15px] font-semibold leading-tight ${t.ov ? 'text-amber-900' : t.value === '—' ? 'text-slate-400' : 'text-slate-900'}`}>{t.value}</div>
                  {t.value === '—' && <div className="text-[10px] italic text-slate-400">Not configured</div>}
                </div>
              </div>
            ))}
            <div className="tcp-info-tile bg-amber-50/50 border-amber-100" data-testid={`${testIdPrefix}-instructions`}>
              <span className="tcp-tile tcp-tile-amber" style={{ width: 30, height: 30 }}><ListChecks className="w-4 h-4" /></span>
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-slate-500">Instructions</div>
                {hasInstr ? (
                  <div className="text-[15px] font-semibold leading-tight text-amber-900" data-testid={`${testIdPrefix}-instructions-status`}>Provided</div>
                ) : (
                  <>
                    <div className="text-[15px] font-semibold leading-tight text-slate-400">—</div>
                    <div className="text-[10px] italic text-slate-400">Not configured</div>
                  </>
                )}
              </div>
            </div>
          </div>
          {taxPeriod === 'delinquent' && (
            <span className="sr-only" data-testid={`${testIdPrefix}-delinquent-scope-note`}>
              Property-jurisdiction overrides are not applied to delinquent defaults — values shown are the delinquent collector's own configuration.
            </span>
          )}
          {hasInstr && (
            <div
              key={`${collectorId}-${instrField.value}`}
              className="tcp-instr-callout tcp-fade-in"
              data-testid={`${testIdPrefix}-instructions-body`}
            >
              <span className="tcp-tile tcp-tile-amber" style={{ width: 30, height: 30 }}><ListChecks className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="tcp-instr-label">Instructions</div>
                <div className="tcp-instr-text">{instrField.value}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationalDefaultsStrip;
